'use client'

import {
    AppliedTemplate,
    applyTemplate,
    getTemplates,
    Template,
    TemplateCategory,
} from '@/lib/api/templates/template-api'
import { useAuth } from '@/store/hooks'
import { useEffect, useState } from 'react'
import { toast } from 'react-toastify'

interface Props {
    category: TemplateCategory
    /** Paciente do documento — é dele que saem `{{paciente}}`, a idade, o CPF. */
    clientId: number
    /** Quem assina. Ausente = o dono do modelo. */
    professionalId?: number | null
    /** CID do documento, quando a tela já tem um. Ausente = o do modelo. */
    icdCodeId?: number | null
    /** Recebe o conteúdo com as variáveis JÁ trocadas. */
    onApply: (applied: AppliedTemplate) => void
    label?: string
}

/**
 * Seletor de modelo no topo de um documento.
 *
 * Passa pelo endpoint `apply` em vez de copiar `template.content`: o texto guardado
 * tem `{{paciente}}`, e quem sabe trocar é o backend — a mesma função serve à tela e
 * ao PDF. Copiar o conteúdo cru é o que fazia a barra de variáveis ser decorativa:
 * o médico clicava em "Paciente" ao montar o modelo e a chave saía impressa no
 * documento entregue ao paciente.
 *
 * Some quando não há modelo: um seletor vazio no topo do formulário é ruído para
 * quem nunca cadastrou nenhum.
 */
export default function TemplatePicker({
    category,
    clientId,
    professionalId,
    icdCodeId,
    onApply,
    label = 'Aplicar modelo',
}: Props) {
    const { user, isProfessional } = useAuth()
    const [templates, setTemplates] = useState<Template[]>([])
    const [applying, setApplying] = useState(false)
    const [selectedId, setSelectedId] = useState('')

    const ownerId =
        professionalId ??
        (isProfessional ? (user?.professionalId ?? null) : null)

    useEffect(() => {
        loadTemplates()
    }, [category, ownerId])

    const loadTemplates = async () => {
        try {
            const response = await getTemplates({
                category,
                professionalId: ownerId ?? undefined,
            })
            if (response.success) setTemplates(response.result || [])
        } catch (error) {
            // Sem modelo o seletor some; o documento manual continua igual.
        }
    }

    const handleChange = async (templateId: string) => {
        setSelectedId(templateId)
        if (!templateId) return

        setApplying(true)
        try {
            const response = await applyTemplate(parseInt(templateId, 10), {
                clientId,
                professionalId: professionalId ?? undefined,
                icdCodeId: icdCodeId ?? undefined,
            })

            if (response.success && response.result) {
                onApply(response.result)
                toast.success('Modelo aplicado')
            } else {
                toast.error(response.error || 'Erro ao aplicar o modelo')
            }
        } catch (error) {
            toast.error('Erro ao aplicar o modelo')
        } finally {
            setApplying(false)
        }
    }

    if (templates.length === 0) return null

    return (
        <div className="form-control w-full">
            <label className="label pb-1">
                <span className="label-text font-medium">{label}</span>
            </label>
            <select
                className="select select-bordered w-full"
                value={selectedId}
                onChange={event => handleChange(event.target.value)}
                disabled={applying}
            >
                <option value="">
                    {applying ? 'Aplicando...' : 'Escolha um modelo...'}
                </option>
                {templates.map(template => (
                    <option key={template.id} value={template.id}>
                        {template.name}
                    </option>
                ))}
            </select>
            <span className="text-xs text-base-content/50 mt-1">
                O texto entra com o nome do paciente e a data já preenchidos.
            </span>
        </div>
    )
}
